import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const safetyTips = [
  {
    icon: "ri-shield-check-line",
    title: "Weryfikacja sprzedawcy",
    desc: "Zawsze sprawdzaj historię sprzedawcy, jego oceny i opinie innych kupujących przed dokonaniem zakupu. Zweryfikowani sprzedawcy przechodzą wieloetapowy proces sprawdzania.",
  },
  {
    icon: "ri-lock-line",
    title: "Bezpieczne płatności",
    desc: "Korzystaj wyłącznie z oficjalnych bramek płatniczych oferowanych przez platformę. Nigdy nie dokonuj przelewów bezpośrednio na konta sprzedawców spoza systemu.",
  },
  {
    icon: "ri-eye-line",
    title: "Zdjęcia QC",
    desc: "Przed wysyłką zawsze proś o zdjęcia kontrolne QC. Pozwolą Ci one zweryfikować rzeczywisty stan produktu i uniknąć nieprzyjemnych niespodzianek.",
  },
  {
    icon: "ri-file-list-3-line",
    title: "Dokumentacja zamówień",
    desc: "Zachowuj całą korespondencję ze sprzedawcami oraz potwierdzenia transakcji. W razie problemów będą one niezbędne do złożenia skutecznej reklamacji.",
  },
  {
    icon: "ri-alert-line",
    title: "Czerwone flagi",
    desc: "Uważaj na podejrzanie niskie ceny, presję na szybką transakcję i konta bez historii. To najczęstsze sygnały ostrzegawcze potencjalnych oszustw.",
  },
  {
    icon: "ri-chat-check-line",
    title: "Komunikacja",
    desc: "Całą komunikację prowadź przez oficjalny czat platformy. Unikaj kontaktu przez zewnętrzne komunikatory, gdzie platforma nie może zapewnić ochrony.",
  },
];

export default function BezpieczenstwoPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuypolska.rest';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const pageUrl = `${siteUrl}/bezpieczenstwo`;

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: pageUrl,
    name: 'Czy Kakobuy jest bezpieczny? Kompletny przewodnik bezpieczeństwa',
    description: 'Wszystko co musisz wiedzieć o bezpieczeństwie zakupów na platformie Kakobuy. Praktyczne porady, zasady bezpieczeństwa i sprawdzone strategie ochrony kupujących.',
    inLanguage: 'pl',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Kakobuy Polska Spreadsheet',
      url: siteUrl,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Strona główna', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Bezpieczeństwo', item: pageUrl },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <main className="pt-16">
        <div className="relative w-full h-[350px] md:h-[420px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Editorial%20security%20and%20trust%20concept%20photography%20with%20soft%20warm%20lighting%2C%20modern%20clean%20composition%20featuring%20protective%20symbols%2C%20lock%20and%20shield%20motifs%20in%20subtle%20abstract%20forms%2C%20cream%20and%20sage%20green%20tones%2C%20professional%20yet%20approachable%20atmosphere%2C%20high-end%20editorial%20style%20with%20gentle%20shadows%20and%20flowing%20organic%20shapes&width=1800&height=900&seq=safety-hero-01&orientation=landscape"
            alt="Bezpieczeństwo Kakobuy"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-background-50"></div>

          <div className="absolute bottom-0 left-0 right-0 z-10 pb-10">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="max-w-[700px]">
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                  Czy Kakobuy jest bezpieczny? Kompletny przewodnik
                </h1>
                <p className="text-base md:text-lg text-white/90 leading-relaxed">
                  Wszystko co musisz wiedzieć o bezpieczeństwie zakupów na platformie Kakobuy. Praktyczne porady i sprawdzone strategie.
                </p>
              </div>
            </div>
          </div>
        </div>

        <section className="w-full py-10 md:py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-3">
                Zasady bezpieczeństwa Kakobuy
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-[600px] mx-auto">
                Poznaj kluczowe zasady, które pomogą Ci bezpiecznie robić zakupy i unikać potencjalnych zagrożeń
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {safetyTips.map((tip, index) => (
                <div
                  key={index}
                  className="group p-6 md:p-7 bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all duration-200"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-5 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-200">
                    <i className={`${tip.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3">
                    {tip.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {tip.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-16 bg-background-100">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-8 text-center">
              System ochrony kupujących
            </h2>

            <div className="space-y-6">
              <div className="prose-custom">
                <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">Jak działa ochrona kupujących?</h3>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  Kakobuy oferuje kompleksowy system ochrony kupujących, który działa na kilku poziomach. Platforma wstrzymuje płatność sprzedawcy do momentu, gdy potwierdzisz otrzymanie i zadowolenie z produktu. W przypadku problemów, masz prawo do złożenia reklamacji i otrzymania pełnego lub częściowego zwrotu środków.
                </p>

                <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">Proces reklamacji</h3>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  Proces reklamacyjny na Kakobuy jest przejrzysty i przyjazny dla użytkownika. Wystarczy zgłosić problem przez platformę, dołączyć zdjęcia dokumentujące wadę lub niezgodność, a zespół Kakobuy zajmie się sprawą. Większość reklamacji jest rozpatrywana w ciągu 3-7 dni roboczych.
                </p>

                <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">Kiedy możesz liczyć na zwrot?</h3>
                <ul className="list-disc list-inside space-y-2 text-sm md:text-base text-foreground-700 mb-4">
                  <li>Produkt nie został wysłany w ustalonym terminie</li>
                  <li>Otrzymany produkt znacząco różni się od opisu lub zdjęć</li>
                  <li>Produkt jest uszkodzony lub wadliwy</li>
                  <li>Otrzymałeś niekompletne zamówienie</li>
                  <li>Sprzedawca nie odpowiada na wiadomości</li>
                </ul>

                <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">Bezpieczeństwo danych</h3>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  Kakobuy stosuje zaawansowane protokoły szyfrowania SSL do ochrony wszystkich danych przesyłanych przez platformę. Dane płatnicze są przetwarzane przez certyfikowane bramki płatnicze, a platforma nie przechowuje pełnych danych kart kredytowych. Twoje dane osobowe są chronione zgodnie z międzynarodowymi standardami bezpieczeństwa.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              Kupuj bezpiecznie z Kakobuy Spreadsheet
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-[560px] mx-auto mb-8">
              Nasz arkusz zawiera tylko zweryfikowane produkty od sprawdzonych sprzedawców
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 rounded-md text-base font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Otwórz Kakobuy Spreadsheet
                <i className="ri-arrow-right-line"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-100 text-foreground-800 rounded-md text-base font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Strona główna
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}